// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React from 'react';
import IconImage from 'components/treactui-template/icons/IconImage';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import ParamsWithId from 'model/ParamsWithId';
import LabelColorRect from './LabelColorRect';
import ProcessTag from 'components/common/ProcessTag';
import { DatasetFilterType, QCResult } from './filter/filters-defaults';
import DatasetTag from 'components/common/DatasetTag';
import useIsQC from 'hooks/useIsQC';
import IconLabel from 'components/treactui-template/icons/IconLabel';
import { useTranslation } from 'react-i18next';
import useProjectLabels from 'hooks/useProjectLabels';
import useLabelSummary from 'hooks/useLabelSummary';
import { LabelPackageInfoMessage } from '@tls/sw91-communication/types/com.api_db_access';
import { Colors, OutputFooter } from '@tls/treact-ui';
import { IconCircle } from '@tls/treact-ui/icons';
import TestableLoader from 'components/treactui-template/atoms/TestableLoader';

interface Props {
  labels: LabelPackageInfoMessage[];
}

export default function DataFooter({ labels }: Readonly<Props>) {
  const { id } = useParams<ParamsWithId>();
  const { selectedProject } = useProjectsMetadata(id);
  const labelSummary = useLabelSummary(labels);
  const { t } = useTranslation();
  const { loading: projectLabelsLoading } = useProjectLabels();

  const modelClassesWithoutBackground =
    selectedProject?.projectUseCase?.modelClasses?.filter(modelClass => modelClass.classIndex !== 0) ?? [];

  const isQC = useIsQC();

  if (projectLabelsLoading) {
    return (
      <Root>
        <TestableLoader label='' data-testid='data-footer-loader' />
      </Root>
    );
  }

  return (
    <Root>
      <Section>
        <div>{t('project.data.footer.dataSet')}</div>
        <SectionData>
          <DataItem data-testid='data-footer-total-count'>
            <IconImage height={'30px'} />
            {labels.length}
          </DataItem>
          {isQC ? (
            <>
              <DataItem data-testid='data-footer-training-set-count'>
                <DatasetTag size='30px' dataset={DatasetFilterType.TrainingSet} />
                {labelSummary.traingSetCount}
              </DataItem>
              <DataItem data-testid='data-footer-evaluation-set-count'>
                <DatasetTag size='30px' dataset={DatasetFilterType.EvaluationSet} />
                {labelSummary.evaluationSetCount}
              </DataItem>
            </>
          ) : (
            <DataItem data-testid='data-footer-labeled-count'>
              <IconLabelContainer>
                <IconLabel height={'24px'} />
              </IconLabelContainer>
              {labelSummary.traingSetCount + labelSummary.evaluationSetCount}
            </DataItem>
          )}
          <DataItem data-testid='data-footer-unlabeled-count'>
            <IconCircle strokeWidth='4px' viewBox='-2 -2 40 40' width={'24px'} />
            {labelSummary.unlabeldCount}
          </DataItem>
        </SectionData>
      </Section>
      {isQC && (
        <>
          {SectionDivider}
          <Section>
            <div>{t('project.data.footer.processTags')}</div>
            <SectionData>
              <DataItem data-testid='data-footer-process-good-count'>
                <ProcessTag size={'30px'} processTag={QCResult.Good} />
                {labelSummary.goodProcessCount}
              </DataItem>
              <DataItem data-testid='data-footer-process-bad-count'>
                <ProcessTag size={'30px'} processTag={QCResult.Bad} />
                {labelSummary.badProcessCount}
              </DataItem>
              <DataItem data-testid='data-footer-process-undefined-count'>
                <ProcessTag size={'30px'} processTag={QCResult.Undefined} />
                {labelSummary.undefinedProcessCount}
              </DataItem>
            </SectionData>
          </Section>
        </>
      )}
      {SectionDivider}
      <Section>
        <div>{t('project.data.footer.labels')}</div>
        <SectionData>
          {modelClassesWithoutBackground.map((modelClass, index) => {
            const usedLabels = labels.filter(label => label.classIndicesInMask.includes(index + 1));
            return (
              <React.Fragment key={modelClass.className}>
                <DataItem
                  key={`item-${modelClass.className}`}
                  data-testid={`data-footer-label-${modelClass.classIndex}-count`}
                >
                  <LabelColorRect isUsedLabel labelColor={modelClass.labelColor} size={'24px'} />
                  {usedLabels.length}
                </DataItem>
                <DataItemAdditionalInfo key={`item-additional-${modelClass.className}`}>
                  <div>{modelClass.className}</div>
                </DataItemAdditionalInfo>
              </React.Fragment>
            );
          })}
        </SectionData>
      </Section>
    </Root>
  );
}

const Root = styled(OutputFooter)`
  display: flex;
  width: 100%;
  justify-content: start;
  z-index: 0;
  height: 6.5rem;
`;
const Section = styled.div`
  height: 100%;
`;
const SectionData = styled.div`
  margin-top: 0.5rem;
  display: flex;
  height: 100%;
`;
const DataItem = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-right: 0.5rem;
  margin-left: 0.5rem;
`;

const DataItemAdditionalInfo = styled(DataItem)`
  margin-left: 0;
  justify-content: start;
  align-items: start;
  height: 80px;
  max-width: 200px;
  margin-top: 4px;
  margin-right: 8px;

  div {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
    width: 100%;
  }
`;

const Divider = styled.div`
  height: calc(100% - 2rem);
  width: 1px;
  margin: 1rem 0 0 0;
  background-color: ${Colors.trgrey2.hex};
`;

const IconLabelContainer = styled.div`
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectionDivider = (
  <Section>
    <SectionData>
      <DataItem>
        <Divider />
      </DataItem>
    </SectionData>
  </Section>
);
